import { Modal, View, Text, TouchableOpacity, ScrollView, StyleSheet, ActivityIndicator } from 'react-native';
import { useExerciseHistory } from '@/hooks/useExerciseHistory';
import { ProgressChart } from '@/components/ProgressChart';

interface Props {
  exerciseId: string | null;
  exerciseName: string;
  studentId: string;
  onClose: () => void;
}

export function ExerciseHistoryModal({ exerciseId, exerciseName, studentId, onClose }: Props) {
  const { history, loading } = useExerciseHistory(exerciseId ?? '', studentId);

  const chartData = history.map(h => ({ value: h.load, label: h.label }));
  const firstEntry = history[0];
  const lastEntry = history[history.length - 1];
  const prevEntry = history[history.length - 2];
  const maxEntry = history.reduce((a, b) => (b.load > a.load ? b : a), history[0] ?? { load: 0, label: '' });

  const gainTotal = lastEntry && firstEntry ? lastEntry.load - firstEntry.load : 0;
  const gainTotalPct = firstEntry && firstEntry.load > 0 ? (gainTotal / firstEntry.load) * 100 : 0;
  const gainSinceLast = lastEntry && prevEntry ? lastEntry.load - prevEntry.load : 0;

  return (
    <Modal visible={!!exerciseId} animationType="slide" onRequestClose={onClose}>
      <View style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={onClose} style={styles.backBtn}>
            <Text style={styles.backBtnText}>← Voltar</Text>
          </TouchableOpacity>
          <Text style={styles.title} numberOfLines={1}>{exerciseName}</Text>
        </View>

        {loading ? (
          <ActivityIndicator style={{ flex: 1 }} color="#C8102E" />
        ) : (
          <ScrollView contentContainerStyle={styles.content}>
            {history.length > 0 ? (
              <>
                <View style={styles.statsRow}>
                  <View style={styles.statBox}>
                    <Text style={styles.statLabel}>ÚLTIMA CARGA</Text>
                    <Text style={styles.statValue}>{lastEntry?.load ?? 0}<Text style={styles.statUnit}>kg</Text></Text>
                  </View>
                  <View style={styles.statBox}>
                    <Text style={styles.statLabel}>MÁXIMO</Text>
                    <Text style={styles.statValue}>{maxEntry?.load ?? 0}<Text style={styles.statUnit}>kg</Text></Text>
                  </View>
                  <View style={styles.statBox}>
                    <Text style={styles.statLabel}>REGISTROS</Text>
                    <Text style={styles.statValue}>{history.length}</Text>
                  </View>
                </View>

                <ProgressChart
                  data={chartData}
                  title="Evolução da carga (kg)"
                  minPoints={1}
                />

                {history.length >= 2 && (
                  <View style={styles.evolutionCard}>
                    <Text style={styles.sectionLabel}>EVOLUÇÃO</Text>
                    <View style={styles.evolutionRow}>
                      <View style={styles.evolutionItem}>
                        <Text style={styles.evolutionLabel}>Total</Text>
                        <Text style={[styles.evolutionValue, gainTotal >= 0 ? styles.up : styles.down]}>
                          {gainTotal >= 0 ? '+' : ''}{gainTotal.toFixed(1)} kg
                        </Text>
                        <Text style={styles.evolutionSub}>
                          {gainTotalPct >= 0 ? '+' : ''}{gainTotalPct.toFixed(1)}% desde o 1º treino
                        </Text>
                      </View>
                      <View style={styles.evolutionItem}>
                        <Text style={styles.evolutionLabel}>Desde o último</Text>
                        <Text style={[styles.evolutionValue, gainSinceLast >= 0 ? styles.up : styles.down]}>
                          {gainSinceLast >= 0 ? '+' : ''}{gainSinceLast.toFixed(1)} kg
                        </Text>
                        <Text style={styles.evolutionSub}>
                          {gainSinceLast > 0 ? 'progrediu' : gainSinceLast < 0 ? 'reduziu' : 'manteve'}
                        </Text>
                      </View>
                    </View>
                  </View>
                )}

                <Text style={styles.sectionLabel}>HISTÓRICO</Text>
                {[...history].reverse().map((entry, i) => (
                  <View key={i} style={styles.historyRow}>
                    <Text style={styles.historyDate}>{entry.label}</Text>
                    <Text style={styles.historyLoad}>{entry.load} kg</Text>
                  </View>
                ))}
              </>
            ) : (
              <View style={styles.empty}>
                <Text style={styles.emptyText}>Nenhum registro de carga para este exercício ainda.</Text>
              </View>
            )}
          </ScrollView>
        )}
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F5F5F5' },
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingTop: 14, paddingBottom: 12, backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#eee', gap: 12 },
  backBtn: { flexShrink: 0 },
  backBtnText: { color: '#C8102E', fontSize: 15, fontWeight: '600' },
  title: { flex: 1, fontSize: 16, fontWeight: '700', color: '#1A1A1A' },
  content: { padding: 16, paddingBottom: 40 },
  statsRow: { flexDirection: 'row', gap: 10, marginBottom: 16 },
  statBox: { flex: 1, backgroundColor: '#fff', borderRadius: 10, padding: 12, alignItems: 'center', borderWidth: 1, borderColor: '#eee' },
  statLabel: { fontSize: 9, fontWeight: '700', color: '#aaa', letterSpacing: 0.5, marginBottom: 4 },
  statValue: { fontSize: 22, fontWeight: '800', color: '#1A1A1A' },
  statUnit: { fontSize: 13, fontWeight: '500', color: '#888' },
  sectionLabel: { fontSize: 11, fontWeight: '700', color: '#888', letterSpacing: 0.8, marginTop: 8, marginBottom: 10 },
  historyRow: { flexDirection: 'row', justifyContent: 'space-between', backgroundColor: '#fff', padding: 12, borderRadius: 8, marginBottom: 6 },
  historyDate: { fontSize: 13, color: '#555' },
  historyLoad: { fontSize: 13, fontWeight: '700', color: '#1A1A1A' },
  empty: { flex: 1, alignItems: 'center', justifyContent: 'center', paddingTop: 80 },
  emptyText: { color: '#aaa', textAlign: 'center', fontSize: 15 },

  evolutionCard: { backgroundColor: '#fff', borderRadius: 10, padding: 14, marginTop: 12, marginBottom: 16, borderWidth: 1, borderColor: '#eee' },
  evolutionRow: { flexDirection: 'row', gap: 12 },
  evolutionItem: { flex: 1 },
  evolutionLabel: { fontSize: 11, color: '#888', fontWeight: '600', marginBottom: 4 },
  evolutionValue: { fontSize: 22, fontWeight: '800' },
  evolutionSub: { fontSize: 11, color: '#888', marginTop: 2 },
  up: { color: '#2E7D32' },
  down: { color: '#C8102E' },
});
